<i18n locale="en">
{
  "asap": "ASAP (15 min)",
  "callouts": {
    "app": "Branded mobile app",
    "photo": "Photo upscaling",
    "reviews": "5 star reviews",
    "sms": "Automated SMS marketing"
  },
  "delivery": "Delivery",
  "explore": "Explore",
  "milestone-100": "100",
  "milestone-500": "500",
  "pickup": "Pickup",
  "points": "400 points",
  "rewards": "Earn rewards",
  "rewards-prefix": "You’re",
  "rewards-suffix": "away from your first rewards."
}
</i18n>
<template>
  <div
    :class="[
      'personalized-mobile-app-preview',
      { 'personalized-mobile-app-preview--compact': compact },
    ]"
    :data-testid="testId"
  >
    <div class="personalized-mobile-app-preview__scale">
      <div class="personalized-mobile-app-preview__phone">
        <div class="personalized-mobile-app-preview__screen">
          <img
            :src="data.heroImageUrl"
            :alt="emptyAlt"
            class="personalized-mobile-app-preview__hero-image"
            :data-testid="`${testId}-hero-image`"
          />
          <div class="personalized-mobile-app-preview__hero-fade" />
          <span class="personalized-mobile-app-preview__profile">
            <PhUser :size="16" weight="fill" />
          </span>

          <div class="personalized-mobile-app-preview__content">
            <div class="personalized-mobile-app-preview__identity">
              <p
                class="personalized-mobile-app-preview__brand-name"
                :data-testid="`${testId}-brand-name`"
              >
                {{ data.brandName }}
              </p>
              <p
                class="personalized-mobile-app-preview__address"
                :data-testid="`${testId}-address`"
              >
                {{ data.address }}
              </p>
              <p class="personalized-mobile-app-preview__status">
                <ClockFilled :size="10" />
                {{ data.statusLabel }}
              </p>
            </div>

            <div class="personalized-mobile-app-preview__fulfillment">
              <span
                class="personalized-mobile-app-preview__fulfillment--active"
                >{{ t('pickup') }}</span
              >
              <span>{{ t('delivery') }}</span>
            </div>
            <div class="personalized-mobile-app-preview__details">
              <div class="personalized-mobile-app-preview__detail">
                <Store4Filled :size="12" />
                <span>{{ data.locationName }}</span>
                <ChevronRightSmall :size="16" />
              </div>
              <div class="personalized-mobile-app-preview__detail">
                <ZapFilled :size="13" />
                <span>{{ t('asap') }}</span>
                <ChevronRightSmall :size="16" />
              </div>
            </div>

            <div class="personalized-mobile-app-preview__rewards-heading">
              <strong>{{ t('rewards') }}</strong>
              <div>
                <span>{{ t('explore') }}</span>
                <ChevronRightSmall :size="16" />
              </div>
            </div>
            <div class="personalized-mobile-app-preview__rewards-card">
              <div class="personalized-mobile-app-preview__rewards-top">
                <div class="personalized-mobile-app-preview__reward-images">
                  <img
                    v-for="index in 3"
                    :key="index"
                    :src="data.heroImageUrl"
                    :alt="emptyAlt"
                  />
                </div>
                <div class="personalized-mobile-app-preview__progress">
                  <span /><i /><i /> <small>{{ t('milestone-100') }}</small
                  ><small>{{ t('milestone-500') }}</small>
                </div>
              </div>
              <p>
                {{ t('rewards-prefix') }} <strong>{{ t('points') }}</strong>
                {{ t('rewards-suffix') }}
              </p>
            </div>
          </div>
        </div>
        <img
          :src="iphoneFrame"
          :alt="emptyAlt"
          class="personalized-mobile-app-preview__frame"
          :data-testid="`${testId}-iphone-frame`"
        />
      </div>

      <span
        class="personalized-mobile-app-preview__callout personalized-mobile-app-preview__callout--app"
      >
        <Phone :size="15" />
        {{ t('callouts.app') }}
      </span>
      <span
        class="personalized-mobile-app-preview__callout personalized-mobile-app-preview__callout--reviews"
      >
        <Star :size="15" />
        {{ t('callouts.reviews') }}
      </span>
      <span
        class="personalized-mobile-app-preview__callout personalized-mobile-app-preview__callout--sms"
      >
        <BubbleSparkle :size="15" />
        {{ t('callouts.sms') }}
      </span>
      <span
        class="personalized-mobile-app-preview__callout personalized-mobile-app-preview__callout--photo"
      >
        <FocusMagic :size="15" />
        {{ t('callouts.photo') }}
      </span>
      <div class="personalized-mobile-app-preview__bottom-fade" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BubbleSparkle } from '@owner/icons-vue/BubbleSparkle'
import { ChevronRightSmall } from '@owner/icons-vue/ChevronRightSmall'
import { ClockFilled } from '@owner/icons-vue/ClockFilled'
import { FocusMagic } from '@owner/icons-vue/FocusMagic'
import { Phone } from '@owner/icons-vue/Phone'
import { Star } from '@owner/icons-vue/Star'
import { Store4Filled } from '@owner/icons-vue/Store4Filled'
import { ZapFilled } from '@owner/icons-vue/ZapFilled'
import { PhUser } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'

import iphoneFrame from '../assets/iphone-frame.png'
import type { PersonalizedProUpsellPreviewData } from '../composables/usePersonalizedProUpsellPreview'

const { t } = useI18n()
const emptyAlt = ''

withDefaults(
  defineProps<{
    compact?: boolean
    data: PersonalizedProUpsellPreviewData
    testId: string
  }>(),
  { compact: false }
)
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';

.personalized-mobile-app-preview {
  width: 100%;
  max-width: 560px;
  height: 550px;
  overflow: hidden;

  &--compact {
    width: 70%;
    max-width: none;
    height: 400px;

    .personalized-mobile-app-preview__scale {
      max-width: none;
      height: 400px;
    }

    .personalized-mobile-app-preview__phone {
      transform: translateX(-50%) scale(0.8);
      transform-origin: top center;
    }
  }

  @include breakpoints.respond-to('sm-and-down') {
    &--compact {
      width: 100%;
      height: 330px;

      .personalized-mobile-app-preview__scale {
        height: 330px;
      }

      .personalized-mobile-app-preview__phone {
        transform: translateX(-50%) scale(0.6);
      }

      .personalized-mobile-app-preview__callout {
        transform: scale(0.72);

        &--app {
          top: 25px;
          left: 38px;
          transform-origin: top left;
        }

        &--reviews {
          top: 75px;
          right: 12px;
          transform-origin: top right;
        }

        &--sms {
          top: 140px;
          left: 8px;
          transform-origin: top left;
        }

        &--photo {
          top: 210px;
          right: 6px;
          transform-origin: top right;
        }
      }
    }
  }

  &__scale {
    position: relative;
    width: 100%;
    max-width: 560px;
    height: 550px;
    overflow: hidden;
  }

  &__phone {
    position: absolute;
    top: 0;
    left: 50%;
    width: 390px;
    height: 844px;
    transform: translateX(-50%);
  }

  &__screen {
    position: absolute;
    inset: 20px 32px 38px 10px;
    overflow: hidden;
    border-radius: 48px;
    background: var(--white);
    transform: perspective(1000px) rotateY(-5deg);
  }

  &__hero-image {
    position: absolute;
    top: 0;
    width: 100%;
    height: 280px;
    object-fit: cover;
  }

  &__hero-fade {
    position: absolute;
    top: 0;
    width: 100%;
    height: 340px;
    background: linear-gradient(
      to bottom,
      rgb(255 255 255 / 0%) 18%,
      rgb(255 255 255 / 74%) 50%,
      var(--white) 78%
    );
  }

  &__profile {
    position: absolute;
    top: 78px;
    left: 20px;
    display: grid;
    width: 36px;
    height: 36px;
    place-items: center;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
    background: var(--white);
    color: #272a31;
    opacity: 0.9;
  }

  &__content {
    position: absolute;
    top: 160px;
    right: 20px;
    left: 20px;
    color: #3d3f47;
  }

  &__identity {
    text-align: center;
  }

  &__brand-name,
  &__address,
  &__status,
  &__rewards-card p {
    margin: 0;
  }

  &__brand-name {
    overflow: hidden;
    color: #272a31;
    font-size: 18px;
    font-weight: 500;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__address {
    overflow: hidden;
    color: #53555d;
    font-size: 10px;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__status {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    color: #149959;
    font-size: 10px;
    font-weight: 500;
    line-height: 13px;

    :deep(svg) {
      flex: none;
    }
  }

  &__fulfillment {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 46px;
    margin-top: 12px;
    padding: 1px;
    border-radius: 24px;
    background: #f3f3f5;
    font-size: 12px;
    text-align: center;

    span {
      display: grid;
      place-items: center;
    }

    &--active {
      border: 1px solid #e5e5e5;
      border-radius: 23px;
      background: var(--white);
      box-shadow: 0 0 0 1px #e5e5e5;
      color: #272a31;
      font-weight: 500;
    }
  }

  &__details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
  }

  &__detail {
    display: flex;
    gap: 6px;
    align-items: center;
    min-width: 0;
    height: 36px;
    padding: 0 10px;
    overflow: hidden;
    border-radius: 12px;
    background: #f3f3f5;
    font-size: 10px;
    font-weight: 500;
    white-space: nowrap;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    :deep(svg) {
      flex: none;

      &:last-child {
        margin-left: auto;
      }
    }
  }

  &__rewards-heading {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
    color: #272a31;
    font-size: 15px;

    > div {
      display: flex;
      gap: 4px;
      align-items: center;

      :deep(svg) {
        flex: none;
      }
    }

    > div span {
      color: #4d5058;
      font-size: 12px;
      font-weight: 500;
    }
  }

  &__rewards-card {
    display: grid;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
    border-radius: 16px;
    background: #f7f8fa;
    font-size: 11px;
    line-height: 15px;
    text-align: center;
  }

  &__rewards-top,
  &__reward-images {
    display: flex;
    align-items: center;
  }

  &__rewards-top {
    gap: 12px;
  }

  &__reward-images img {
    width: 34px;
    height: 34px;
    margin-left: -6px;
    border: 1px solid var(--white);
    border-radius: 14px;
    object-fit: cover;
  }

  &__progress {
    position: relative;
    flex: 1;
    height: 3px;
    background: #d8d9de;

    span {
      display: block;
      width: 25%;
      height: 3px;
      background: #777a83;
    }

    i {
      position: absolute;
      top: -2.5px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #777a83;

      &:first-of-type {
        left: 25%;
      }
      &:last-of-type {
        left: 75%;
        background: #e8e9ec;
      }
    }

    small {
      position: absolute;
      top: 8px;
      color: #777a83;
      font-size: 10px;

      &:first-of-type {
        left: 19%;
      }
      &:last-of-type {
        left: 69%;
      }
    }
  }

  &__frame {
    position: absolute;
    top: -220px;
    left: -206px;
    z-index: 1;
    width: 809px;
    max-width: none;
    height: 1438px;
    pointer-events: none;
  }

  &__callout {
    position: absolute;
    z-index: 2;
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 10px 15px;
    border-radius: 999px;
    color: var(--white);
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    white-space: nowrap;

    &--app {
      top: 45px;
      left: 60px;
      background: linear-gradient(90deg, #45dc73, #20bd58);
    }
    &--reviews {
      top: 114px;
      right: 20px;
      background: linear-gradient(90deg, #7ecbf0, #408fc2);
    }
    &--sms {
      top: 181px;
      left: 0;
      background: linear-gradient(90deg, #79cef0, #45a6dc);
    }
    &--photo {
      top: 236px;
      right: 80px;
      background: linear-gradient(90deg, #49dc73, #1fbb58);
    }
  }

  &__bottom-fade {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    height: 110px;
    background: linear-gradient(to bottom, rgb(255 255 255 / 0%), var(--white));
    pointer-events: none;
  }
}
</style>
